import ISO6391 from "iso-639-1";
import { CourseRun } from "@/common/courseRun/types";

/**
 * Extracts AI languages from a course run
 * @param courseRun The course run object
 * @returns Array of AI language objects with code and label, or empty array if none available
 */
export const getAiLanguages = (courseRun: CourseRun) => {
  return courseRun.aiLanguages || [];
};

/**
 * Gets a flat list of all translation language codes from AI languages
 * @param courseRun The course run object
 * @returns Array of language codes
 */
export const getAiTranslationLanguageCodes = (
  courseRun: CourseRun,
): string[] => {
  const aiLanguages = getAiLanguages(courseRun);
  return aiLanguages.flatMap((aiLang) =>
    aiLang.translationLanguages
      .map((lang) => lang.code)
      .filter((code): code is string => code !== null),
  );
};

/**
 * Gets a flat list of all translation language labels from AI languages
 * @param courseRun The course run object
 * @returns Array of language labels
 */
export const getAiTranslationLanguageLabels = (
  courseRun: CourseRun,
): string[] => {
  const aiLanguages = getAiLanguages(courseRun);
  return aiLanguages.flatMap((aiLang) =>
    aiLang.translationLanguages
      .map((lang) => {
        return lang.code
          ? ISO6391.getNativeName(lang.code.slice(0, 2))
          : lang.label;
      })
      .filter((label): label is string => label !== null),
  );
};

/**
 * Checks if a course run has any AI languages available
 * @param courseRun The course run object
 * @returns Boolean indicating if AI languages are available
 */
export const hasAiLanguages = (courseRun: CourseRun): boolean => {
  const aiLanguages = getAiLanguages(courseRun);
  return (
    aiLanguages.length > 0 &&
    aiLanguages.some((aiLang) => aiLang.translationLanguages.length > 0)
  );
};
